/**
 * Road closure detail panel (Ice Storm Risk → "Road Closures"). A raw ingested
 * operational signal from the R.1f `road_closures` table — NOT an actionable
 * incident (no R.3 dispatch actions): closures are ambient context that paints the
 * road-network / ice-storm impact picture (operator-cop-doctrine §4.5).
 *
 * Headlines the roadway + register (full closure vs restriction), with direction /
 * cause / severity / started / expected-end detail and an ingest-provenance footer
 * (source + when we last refreshed the row). Colour is always paired with the
 * register label (§3A.7).
 */
import { useEffect, useState } from 'react';
import type { RoadClosureFeature, RoadClosureDetail } from '@tsr/shared-types';
import { fetchRoadClosureDetail } from './api';
import { roadClosureStyle, ROAD_CLOSURE_SEVERITY_LABEL } from './styles/roadClosureStyles';
import { logAction } from './lib/operatorActions';

function agoLabel(iso: string | null): string {
  if (!iso) return 'unknown';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return 'unknown';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** Absolute local time + relative, when we have a timestamp. */
function whenLabel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleString()} · ${agoLabel(iso)}`;
}

/** A future end reads as a countdown; a past one as "overdue". */
function endLabel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const ms = d.getTime() - Date.now();
  const rel =
    ms >= 0 ? `in ${Math.max(1, Math.round(ms / 3_600_000))}h` : `${agoLabel(iso)} (overdue)`;
  return `${d.toLocaleString()} · ${rel}`;
}

const PROVINCE_LABEL: Record<string, string> = { ON: 'Ontario', AB: 'Alberta', BC: 'British Columbia' };

export function RoadClosureBody({ closure }: { closure: RoadClosureFeature }): JSX.Element {
  const p = closure.properties;
  const [lon, lat] = closure.geometry.coordinates;
  const style = roadClosureStyle(p.is_closure);
  const [detail, setDetail] = useState<RoadClosureDetail | null>(null);

  useEffect(() => {
    logAction('view_road_closure_detail', { closure_id: closure.id, province: p.province });
  }, [closure.id, p.province]);

  useEffect(() => {
    const ctl = new AbortController();
    void fetchRoadClosureDetail(closure.id, ctl.signal)
      .then(setDetail)
      .catch(() => undefined);
    return () => ctl.abort();
  }, [closure.id]);

  const severityLabel = p.severity ? (ROAD_CLOSURE_SEVERITY_LABEL[p.severity] ?? p.severity) : '—';

  return (
    <div className="px-4 py-4 space-y-4" data-testid="road-closure-body">
      <div className="border-l-2 pl-3" style={{ borderColor: style.color }}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">Road closure</p>
        <h2 className="text-base font-semibold">{p.roadway_name || 'Unnamed roadway'}</h2>
        <span
          className="inline-flex items-center gap-1.5 mt-1.5 text-[11px] px-2 py-0.5 rounded font-medium"
          style={{ backgroundColor: `${style.color}22`, color: style.color }}
        >
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: style.color }}
          />
          {style.label}
        </span>
      </div>

      {p.description && <p className="text-sm text-slate-300 leading-relaxed">{p.description}</p>}

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Details</h3>
        <dl className="grid grid-cols-[7rem_1fr] gap-y-1 text-sm">
          <dt className="text-slate-400">Direction</dt>
          <dd>{p.direction || '—'}</dd>
          <dt className="text-slate-400">Type</dt>
          <dd>{p.event_type || '—'}</dd>
          <dt className="text-slate-400">Cause</dt>
          <dd>{p.cause || '—'}</dd>
          <dt className="text-slate-400">Severity</dt>
          <dd>{severityLabel}</dd>
          <dt className="text-slate-400">Province</dt>
          <dd>{PROVINCE_LABEL[p.province] ?? p.province}</dd>
          <dt className="text-slate-400">Started</dt>
          <dd className="text-xs">{whenLabel(p.started_at)}</dd>
          <dt className="text-slate-400">Expected end</dt>
          <dd className="text-xs">{endLabel(p.expected_end_at)}</dd>
          <dt className="text-slate-400">Reported</dt>
          <dd className="text-xs">{whenLabel(p.observed_at)}</dd>
          <dt className="text-slate-400">Position</dt>
          <dd className="font-mono text-xs">
            {lat.toFixed(3)}, {lon.toFixed(3)}
          </dd>
        </dl>
      </section>

      {p.has_segment && (
        <p className="text-[11px] text-slate-400 leading-relaxed">
          The affected road segment is drawn as a solid line on the map (ground-clamped), with the
          callout box marking the closure.
        </p>
      )}

      <footer className="border-t border-slate-800 pt-3 text-[10px] text-slate-500 leading-relaxed space-y-0.5">
        {detail?.properties.source && (
          <div>
            Source: {detail.properties.source}
            {detail.properties.source_record_id ? ` · #${detail.properties.source_record_id}` : ''}
          </div>
        )}
        {detail?.properties.ingested_at && <div>Ingested {agoLabel(detail.properties.ingested_at)}</div>}
        <div>Road-closure feeds — Ontario 511 · Alberta 511 · DriveBC (Open511).</div>
      </footer>
    </div>
  );
}
